import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, Badge, getLeadTypeBadge } from '../../components/ui/Kit';
import ChannelSelector from '../../components/Leads/ChannelSelector';
import { useToast } from '../../components/ui/Toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiMoreVertical, FiClock, FiPhone, FiMove } = FiIcons;

const normalizeKey = (v) => String(v ?? '').trim().toLowerCase();

/**
 * Map column index → lead_status enum.
 * Resolve last two columns first so middle slots (e.g. “Appointment Done”) stay non-terminal.
 */
function stageIndexToLeadStatus(stageIndex, stagesLength) {
  if (stagesLength < 1) return 'new';
  if (stageIndex === stagesLength - 1) return 'closed_lost';
  if (stageIndex === stagesLength - 2) return 'closed_won';
  if (stageIndex <= 0) return 'new';
  if (stageIndex === 1) return 'contacted';
  if (stageIndex === 2) return 'follow_up';
  if (stageIndex === 3) return 'appointment_set';
  return 'follow_up';
}

export default function Pipeline() {
  const { addToast } = useToast();
  const [pipelines, setPipelines] = useState([]);
  const [activePipeline, setActivePipeline] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [channelFilter, setChannelFilter] = useState('');

  const loadData = useCallback(async () => {
    try {
      const leadFilters = channelFilter ? { channel: channelFilter } : {};
      const [pips, allLeads, me] = await Promise.all([
        api.pipelines.getAll(),
        api.leads.getAll(leadFilters),
        api.auth.getCurrentUser(),
      ]);
      setPipelines(pips);
      setActivePipeline((prev) => {
        if (prev && pips.some((p) => p.id === prev.id)) return prev;
        return pips.length > 0 ? pips[0] : null;
      });
      const scoped =
        me?.role === 'setter'
          ? (allLeads || []).filter((l) =>
              (l.assignments || []).some((a) => a.user_id === me.id),
            )
          : allLeads || [];
      setLeads(scoped);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [channelFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDragOverColumn = (e, stageName) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageName);
  };

  const handleDragLeaveColumn = (e) => {
    const related = e.relatedTarget;
    if (related && e.currentTarget.contains(related)) return;
    setDragOverStage(null);
  };

  const handleDropOnStage = async (e, targetStage, activePl, pipelineLeads, statusToStageMap, stages) => {
    e.preventDefault();
    setDragOverStage(null);

    let payload;
    try {
      payload = JSON.parse(e.dataTransfer.getData('application/json') || '{}');
    } catch {
      return;
    }
    const leadId = payload.leadId;
    if (!leadId || !activePl) return;

    const lead = leads.find((l) => l.id === leadId);
    if (!lead || !pipelineLeads.some((l) => l.id === leadId)) return;

    const currentStage =
      lead.pipeline_id === activePl.id && lead.pipeline_stage
        ? lead.pipeline_stage
        : statusToStageMap[lead.status] || stages[0];
    if (currentStage === targetStage) return;

    const stageIndex = stages.indexOf(targetStage);
    if (stageIndex < 0) return;

    const newStatus = stageIndexToLeadStatus(stageIndex, stages.length);
    const updates = {
      pipeline_id: activePl.id,
      pipeline_stage: targetStage,
      status: newStatus,
    };

    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, ...updates } : l))
    );

    try {
      const updated = await api.leads.update(leadId, updates);
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, ...updated } : l)));
    } catch (err) {
      addToast({ type: 'error', title: 'Could not move lead', message: err.message || 'Update failed' });
      await loadData();
    }
  };

  if (loading) return <div className="p-8 text-center animate-pulse">Loading pipeline...</div>;
  if (!activePipeline) return <div className="p-8 text-center">No pipelines configured.</div>;

  const stages = Array.isArray(activePipeline.stages)
    ? activePipeline.stages
    : typeof activePipeline.stages === 'string'
      ? (() => {
          try {
            return JSON.parse(activePipeline.stages);
          } catch {
            return [];
          }
        })()
      : [];

  if (!stages.length) {
    return <div className="p-8 text-center text-gray-600">This pipeline has no stages configured.</div>;
  }

  const pipelineKind = normalizeKey(activePipeline.pipeline_type);
  const pipelineName = normalizeKey(activePipeline.name);
  const isRecruitingPipeline =
    pipelineKind === 'recruiting' ||
    pipelineKind === 'recruitment' ||
    pipelineName.includes('recruiting');

  const isRecruitLead = (l) => normalizeKey(l.lead_type) === 'recruit';

  const pipelineLeads = leads.filter((l) =>
    isRecruitingPipeline ? isRecruitLead(l) : !isRecruitLead(l)
  );

  const statusToStageMap = {
    new: stages[0],
    contacted: stages[1],
    follow_up: stages[2],
    appointment_set: stages[3] || stages[2],
    closed_won: stages[stages.length - 2],
    closed_lost: stages[stages.length - 1],
  };

  const resolveLeadStageName = (lead, pl, map) => {
    if (lead.pipeline_id === pl.id && lead.pipeline_stage) {
      return lead.pipeline_stage;
    }
    return map[lead.status] || stages[0];
  };

  const getLeadsForStage = (stageName) =>
    pipelineLeads.filter((l) => resolveLeadStageName(l, activePipeline, statusToStageMap) === stageName);

  return (
    <div className="h-full flex flex-col space-y-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline Board</h1>
          <p className="text-sm text-gray-500">Drag by the handle to move between stages. Click a card to open the lead.</p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-gray-500">Channel</span>
            <ChannelSelector
              value={channelFilter || null}
              onChange={(ch) => setChannelFilter(ch || '')}
              allowEmpty
              size="sm"
            />
          </div>
        </div>
        <div className="flex bg-gray-200 p-1 rounded-lg self-start">
          {pipelines.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActivePipeline(p)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activePipeline.id === p.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-x-auto pb-4">
        <div className="flex gap-4 h-full min-h-[600px]">
          {stages.map((stage, idx) => {
            const stageLeads = getLeadsForStage(stage);
            const isOver = dragOverStage === stage;

            return (
              <div
                key={`${stage}-${idx}`}
                className={`w-80 flex-shrink-0 flex flex-col rounded-lg border transition-shadow ${
                  isOver ? 'bg-primary/5 border-primary ring-2 ring-primary/30' : 'bg-gray-100 border-gray-200'
                }`}
                onDragOver={(e) => handleDragOverColumn(e, stage)}
                onDragLeave={handleDragLeaveColumn}
                onDrop={(e) =>
                  handleDropOnStage(e, stage, activePipeline, pipelineLeads, statusToStageMap, stages)
                }
              >
                <div className="p-3 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                  <h3 className="font-semibold text-gray-800 text-sm">{stage}</h3>
                  <Badge variant="default">{stageLeads.length}</Badge>
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[120px]">
                  {stageLeads.map((lead) => (
                    <div key={lead.id} className="flex gap-1 items-stretch">
                      <button
                        type="button"
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData(
                            'application/json',
                            JSON.stringify({ leadId: lead.id })
                          );
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragEnd={() => setDragOverStage(null)}
                        className="flex-shrink-0 w-8 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 cursor-grab active:cursor-grabbing touch-none"
                        title="Drag to change stage"
                        aria-label="Drag to move lead to another stage"
                      >
                        <SafeIcon icon={FiMove} className="w-4 h-4" />
                      </button>
                      <Link
                        to={`/leads/${lead.id}`}
                        className="flex-1 min-w-0 block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <Card className="p-3 hover:ring-2 ring-primary/20 transition-all h-full">
                          <div className="flex justify-between items-start mb-2">
                            <p className="font-medium text-sm text-gray-900 truncate pr-2">
                              {lead.first_name} {lead.last_name}
                            </p>
                            <span
                              className="text-gray-400 pointer-events-none"
                              aria-hidden
                            >
                              <SafeIcon icon={FiMoreVertical} className="w-4 h-4" />
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-3 [&_span]:text-[10px] [&_span]:px-1.5 [&_span]:py-0">
                            {getLeadTypeBadge(lead.lead_type)}
                            {lead.call_count > 0 && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <SafeIcon icon={FiPhone} className="w-3 h-3" />
                                {lead.call_count}
                              </span>
                            )}
                          </div>
                          <div className="flex justify-between items-center text-xs text-gray-400 border-t border-gray-100 pt-2">
                            <span className="flex items-center gap-1">
                              <SafeIcon icon={FiClock} className="w-3 h-3" />
                              {new Date(lead.created_at).toLocaleDateString()}
                            </span>
                            <div className="w-5 h-5 rounded-full bg-secondary text-white flex items-center justify-center text-[9px] font-bold">
                              {lead.assignments?.[0]?.user?.full_name?.charAt(0) || 'U'}
                            </div>
                          </div>
                        </Card>
                      </Link>
                    </div>
                  ))}
                  {stageLeads.length === 0 && (
                    <div className="h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 text-sm pointer-events-none">
                      Drop here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}